import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import RichTextToolbar from "./RichTextToolbar";
import { renderMiniMarkdown } from "./LessonBlock";
import { Plus, Trash2, GripVertical, FileText, Youtube, ExternalLink, ListChecks, Pencil, Eye, Columns } from "lucide-react";
import { extractDominantColor } from "../lib/dominantColor";

/* ============================ Playbook edit dialog ============================ */

export function PlaybookDialog({ open, onOpenChange, playbook, onSave }) {
  const [form, setForm] = useState({ title: "", description: "", cover_color: "#0D9488", cover_image_url: "", published: true, admin_only: false, access_tier: "free", price_usd: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (open && playbook) {
      setForm({
        title: playbook.title || "",
        description: playbook.description || "",
        cover_color: playbook.cover_color || "#0D9488",
        cover_image_url: playbook.cover_image_url || "",
        published: playbook.published !== false,
        admin_only: !!playbook.admin_only,
        access_tier: playbook.access_tier || "free",
        price_usd: playbook.price_usd != null ? String(playbook.price_usd) : "",
      });
    }
  }, [open, playbook]);

  // Compress-and-encode the uploaded illustration directly to a data URL so the
  // admin doesn't need external image hosting. Resizes to a max width of 1200px
  // (keeps aspect ratio) and re-encodes as JPEG q0.82 — final payload usually
  // 40-120KB, small enough for the existing PATCH endpoint.
  const onFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      alert("Please upload an image file.");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onerror = reject;
        i.onload = () => resolve(i);
        i.src = dataUrl;
      });
      const maxW = 1200;
      const scale = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      const out = canvas.toDataURL("image/jpeg", 0.82);
      // Sample dominant color from the compressed image and auto-fill
      // `cover_color` so the letterbox background matches the artwork.
      // Skips overriding if extraction fails (returns null).
      const dominant = await extractDominantColor(out);
      setForm((prev) => ({
        ...prev,
        cover_image_url: out,
        cover_color: dominant || prev.cover_color,
      }));
    } catch {
      alert("Could not read that image. Try a different file.");
    } finally {
      setUploading(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const priceRaw = String(form.price_usd || "").trim();
      const priceNum = priceRaw === "" ? null : Number(priceRaw);
      await onSave({
        ...form,
        title: form.title.trim(),
        cover_image_url: form.cover_image_url?.trim() || null,
        // Only send price for premium playbooks; free ignores it
        price_usd: form.access_tier === "premium" && Number.isFinite(priceNum) && priceNum > 0 ? priceNum : null,
      });
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-lg" data-testid="playbook-edit-dialog">
        <DialogHeader>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Playbook Settings</div>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl font-black">Edit Playbook</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs mt-1">Title, description, cover color, and publish state.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              required maxLength={140} className="bg-slate-50 border-slate-200 rounded-none h-11"
              data-testid="playbook-edit-title" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Description</Label>
            <Textarea rows={3} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-slate-50 border-slate-200 rounded-none resize-none"
              data-testid="playbook-edit-description" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Cover Illustration</Label>
            {form.cover_image_url ? (
              <div
                className="relative border border-slate-200 overflow-hidden flex items-center justify-center"
                style={{ backgroundColor: form.cover_color || "#f8fafc" }}
                data-testid="playbook-edit-cover-preview"
              >
                <img src={form.cover_image_url} alt="" className="w-full h-32 object-contain" />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, cover_image_url: "" })}
                  className="absolute top-2 right-2 bg-white/95 border border-slate-200 hover:border-[#FF3B30] hover:text-[#FF3B30] text-slate-700 rounded-none px-2 py-1 font-mono-num text-[10px] uppercase tracking-widest"
                  data-testid="playbook-edit-cover-clear"
                >
                  Remove
                </button>
              </div>
            ) : (
              <label
                htmlFor="playbook-cover-file-input"
                className={`flex items-center justify-center gap-2 border border-dashed border-slate-300 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-600 rounded-none h-24 cursor-pointer transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                data-testid="playbook-edit-cover-upload-zone"
              >
                <span className="font-mono-num text-[11px] uppercase tracking-widest">
                  {uploading ? "Uploading…" : "+ Upload cover image"}
                </span>
              </label>
            )}
            <input
              id="playbook-cover-file-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="playbook-edit-cover-upload"
            />
            <details className="text-[10px] font-mono-num uppercase tracking-widest text-slate-400">
              <summary className="cursor-pointer hover:text-[#0D9488]">or paste a URL</summary>
              <Input value={form.cover_image_url?.startsWith("data:") ? "" : form.cover_image_url}
                onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                onBlur={async (e) => {
                  const url = e.target.value.trim();
                  if (!url || url.startsWith("data:")) return;
                  const dominant = await extractDominantColor(url);
                  if (dominant) setForm((prev) => ({ ...prev, cover_color: dominant }));
                }}
                placeholder="https://…"
                className="mt-1.5 bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num text-xs normal-case tracking-normal"
                data-testid="playbook-edit-cover-url" />
            </details>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Cover Color</Label>
              <Input type="color" value={form.cover_color || "#0D9488"}
                onChange={(e) => setForm({ ...form, cover_color: e.target.value })}
                className="bg-slate-50 border-slate-200 rounded-none h-11 p-1"
                data-testid="playbook-edit-color" />
            </div>
            <label className="flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 cursor-pointer">
              <input type="checkbox" checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="accent-[#0D9488]"
                data-testid="playbook-edit-published" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-slate-800 font-mono-num">Published</span>
            </label>
          </div>
          <label className={`flex items-start gap-2 border rounded-none px-3 py-2.5 cursor-pointer transition-colors ${form.admin_only ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}>
            <input type="checkbox" checked={form.admin_only}
              onChange={(e) => setForm({ ...form, admin_only: e.target.checked })}
              className="accent-amber-500 mt-0.5"
              data-testid="playbook-edit-admin-only" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-800 font-mono-num flex items-center gap-1.5">
                Admin Only
                {form.admin_only && (
                  <span className="text-[9px] font-black text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 tracking-widest">HIDDEN</span>
                )}
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed mt-0.5 normal-case tracking-normal">
                Only admins can see and open this playbook. Moderators and regular users won't see it in the library. Perfect for internal drafts or private SOPs.
              </p>
            </div>
          </label>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Access Tier</Label>
            <select value={form.access_tier || "free"}
              onChange={(e) => setForm({ ...form, access_tier: e.target.value })}
              className="w-full h-11 bg-slate-50 border border-slate-200 rounded-none px-3 text-sm text-slate-900 focus:border-[#0D9488] focus:outline-none"
              data-testid="playbook-edit-access-tier">
              <option value="free">Free — anyone signed in can read</option>
              <option value="premium">Members Only — locked for non-premium users</option>
            </select>
            <p className="text-[10px] text-slate-500 font-mono-num uppercase tracking-widest">
              Preview lessons (marked in the lesson editor) stay unlocked as a teaser.
            </p>
          </div>
          {form.access_tier === "premium" && (
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Per-playbook Price (USD)</Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 font-mono-num text-sm">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price_usd}
                  onChange={(e) => setForm({ ...form, price_usd: e.target.value })}
                  placeholder="e.g. 9.99  (leave blank = Members Only, no individual purchase)"
                  className="bg-slate-50 border-slate-200 rounded-none h-11 font-mono-num tabular-nums"
                  data-testid="playbook-edit-price"
                />
              </div>
              <p className="text-[10px] text-slate-500 font-mono-num uppercase tracking-widest leading-relaxed">
                Leave blank → only global-Premium members can read. Set a price → users can buy just this playbook via PayPal.
              </p>
            </div>
          )}
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800">Cancel</Button>
            <Button type="submit" disabled={saving}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none uppercase tracking-widest font-semibold"
              data-testid="playbook-edit-submit">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ============================ Section dialog ============================ */

export function SectionDialog({ open, onOpenChange, section, onSave }) {
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setTitle(section?.title || ""); }, [open, section]);
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try { await onSave({ title: title.trim() }); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-md" data-testid="section-dialog">
        <DialogHeader>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Section</div>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl font-black">
            {section ? "Edit Section" : "New Section"}
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs mt-1">A section groups related lessons together.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={140}
              className="bg-slate-50 border-slate-200 rounded-none h-11"
              placeholder="Getting Started"
              data-testid="section-title-input" autoFocus />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800">Cancel</Button>
            <Button type="submit" disabled={saving}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none uppercase tracking-widest font-semibold"
              data-testid="section-submit">
              {saving ? "Saving…" : section ? "Save" : "Add Section"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ============================ Lesson dialog (block editor) ============================ */

const BLANK_BLOCK = { text: { type: "text", text: "" }, video: { type: "video", url: "", title: "" }, link: { type: "link", url: "", title: "", description: "" }, supplies: { type: "supplies", items: [{ name: "", url: "", note: "" }] } };

export function LessonDialog({ open, onOpenChange, lesson, onSave }) {
  const [title, setTitle] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [isPreview, setIsPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(lesson?.title || "");
    setBlocks(lesson?.blocks?.length ? lesson.blocks.map((b) => ({ ...b })) : []);
    setIsPreview(!!lesson?.is_preview);
  }, [open, lesson]);

  const addBlock = (type) => setBlocks([...blocks, JSON.parse(JSON.stringify(BLANK_BLOCK[type]))]);
  const removeBlock = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = blocks.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };
  const update = (i, patch) => setBlocks(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const cleaned = blocks
        .filter((b) => {
          if (b.type === "text") return (b.text || "").trim().length > 0;
          if (b.type === "video") return (b.url || "").trim().length > 0;
          if (b.type === "link") return (b.url || "").trim().length > 0;
          if (b.type === "supplies") return Array.isArray(b.items) && b.items.some((it) => (it?.name || "").trim());
          return false;
        })
        .map((b) => {
          if (b.type === "supplies") {
            return { ...b, items: b.items.filter((it) => (it?.name || "").trim()) };
          }
          return b;
        });
      await onSave({ title: title.trim(), blocks: cleaned, is_preview: isPreview });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-2xl lg:max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="lesson-dialog">
        <DialogHeader>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Lesson</div>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl font-black">
            {lesson ? "Edit Lesson" : "New Lesson"}
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs mt-1">Add content blocks — text, video, link cards, and supply lists.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200}
              className="bg-slate-50 border-slate-200 rounded-none h-11"
              placeholder="How to reconstitute BPC-157"
              data-testid="lesson-title-input" autoFocus />
          </div>

          <label className="flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={isPreview}
              onChange={(e) => setIsPreview(e.target.checked)}
              className="accent-[#0D9488]"
              data-testid="lesson-is-preview" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-800 font-mono-num">
              ★ Free Preview — unlocked for non-members
            </span>
          </label>

          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Content Blocks</Label>
            {blocks.length === 0 ? (
              <p className="text-xs text-slate-500 border border-dashed border-slate-200 p-4">
                No blocks yet — add one below.
              </p>
            ) : (
              blocks.map((b, i) => (
                <div key={i} className="border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`lesson-block-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#0D9488]">// {b.type}</div>
                    <div className="flex gap-0.5">
                      <button type="button" onClick={() => move(i, -1)} className="p-1 text-slate-500 hover:text-slate-900" title="Move up" disabled={i === 0}>▲</button>
                      <button type="button" onClick={() => move(i, 1)} className="p-1 text-slate-500 hover:text-slate-900" title="Move down" disabled={i === blocks.length - 1}>▼</button>
                      <button type="button" onClick={() => removeBlock(i)} className="p-1 text-slate-500 hover:text-[#FF3B30]" title="Remove"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                  {b.type === "text" && (
                    <TextBlockEditor
                      value={b.text || ""}
                      onChange={(text) => update(i, { text })}
                    />
                  )}
                  {b.type === "video" && (
                    <div className="space-y-1.5">
                      <Input value={b.url || ""} onChange={(e) => update(i, { url: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=…" className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num" />
                      <Input value={b.title || ""} onChange={(e) => update(i, { title: e.target.value })}
                        placeholder="Caption (optional)" className="bg-slate-50 border-slate-200 rounded-none h-10" />
                    </div>
                  )}
                  {b.type === "link" && (
                    <div className="space-y-1.5">
                      <Input value={b.url || ""} onChange={(e) => update(i, { url: e.target.value })}
                        placeholder="https://…" className="bg-slate-50 border-slate-200 rounded-none h-10 font-mono-num" />
                      <Input value={b.title || ""} onChange={(e) => update(i, { title: e.target.value })}
                        placeholder="Card title" className="bg-slate-50 border-slate-200 rounded-none h-10" />
                      <Textarea rows={2} value={b.description || ""} onChange={(e) => update(i, { description: e.target.value })}
                        placeholder="Description (optional)" className="bg-slate-50 border-slate-200 rounded-none resize-none text-sm" />
                    </div>
                  )}
                  {b.type === "supplies" && (
                    <div className="space-y-1.5">
                      {(b.items || []).map((it, k) => (
                        <div key={k} className="grid grid-cols-[1fr_1fr_auto] gap-1.5">
                          <Input value={it.name || ""} onChange={(e) => update(i, { items: b.items.map((x, kk) => kk === k ? { ...x, name: e.target.value } : x) })}
                            placeholder="Supply name" className="bg-slate-50 border-slate-200 rounded-none h-9 text-sm" />
                          <Input value={it.url || ""} onChange={(e) => update(i, { items: b.items.map((x, kk) => kk === k ? { ...x, url: e.target.value } : x) })}
                            placeholder="Buy link (optional)" className="bg-slate-50 border-slate-200 rounded-none h-9 text-sm font-mono-num" />
                          <button type="button" onClick={() => update(i, { items: b.items.filter((_, kk) => kk !== k) })}
                            className="text-slate-500 hover:text-[#FF3B30] px-1" title="Remove"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <button type="button" onClick={() => update(i, { items: [...(b.items || []), { name: "", url: "", note: "" }] })}
                        className="text-[10px] font-mono-num uppercase tracking-widest text-[#0D9488] hover:underline">
                        + add supply
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
            <div className="flex flex-wrap gap-2">
              <AddBlockButton icon={FileText} label="Text" onClick={() => addBlock("text")} testid="add-block-text" />
              <AddBlockButton icon={Youtube} label="YouTube" onClick={() => addBlock("video")} testid="add-block-video" />
              <AddBlockButton icon={ExternalLink} label="Link Card" onClick={() => addBlock("link")} testid="add-block-link" />
              <AddBlockButton icon={ListChecks} label="Supply List" onClick={() => addBlock("supplies")} testid="add-block-supplies" />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="border-slate-200 bg-transparent hover:bg-slate-50 rounded-none text-slate-800">Cancel</Button>
            <Button type="submit" disabled={saving}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-none uppercase tracking-widest font-semibold"
              data-testid="lesson-submit">
              {saving ? "Saving…" : lesson ? "Save" : "Add Lesson"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddBlockButton({ icon: Icon, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-800 px-3 h-9 font-mono-num text-[11px] uppercase tracking-widest transition-colors">
      <Icon className="w-3.5 h-3.5" /> + {label}
    </button>
  );
}

/**
 * Text block editor: Edit / Preview / Split tabs.
 * - Edit: toolbar + textarea for authoring
 * - Preview: rendered markdown (read-only)
 * - Split (lg+ screens only): textarea on the left, live preview on the right.
 *   Falls back gracefully on narrow screens because the container hides
 *   the Split tab below `lg`.
 */
function TextBlockEditor({ value, onChange }) {
  const ref = useRef(null);
  const [mode, setMode] = useState("edit"); // "edit" | "preview" | "split"
  const tabBase = "inline-flex items-center gap-1.5 px-3 h-8 font-mono-num text-[10px] uppercase tracking-widest transition-colors border-b-2";
  const tabCls = (active) => `${tabBase} ${active ? "border-[#0D9488] text-[#0D9488]" : "border-transparent text-slate-500 hover:text-slate-800"}`;

  // Shared subcomponents so Edit + Split share the same authoring surface.
  const authoring = (
    <>
      <RichTextToolbar textareaRef={ref} value={value} onChange={onChange} />
      <Textarea
        ref={ref}
        rows={mode === "split" ? 14 : 6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border-slate-200 rounded-none resize-y font-mono-num text-sm border-t-0 flex-1"
        placeholder={
          "Type or click a toolbar button.\n\nMarkdown supported:\n# H1  ## H2  ### H3  #### H4\n**bold**  *italic*  ~~strike~~  `code`\n- bullet list\n1. numbered list\n> quote\n---  divider\n[link](url)  ![image](url)  @[video](youtube-url)"
        }
        data-testid="text-block-textarea"
      />
    </>
  );
  const previewPane = (
    <div
      className="bg-white border border-slate-200 rounded-none min-h-[10rem] max-h-[24rem] overflow-y-auto p-4"
      data-testid="text-block-preview"
    >
      {value.trim() ? (
        renderMiniMarkdown(value)
      ) : (
        <div className="text-[11px] font-mono-num uppercase tracking-widest text-slate-400 py-6 text-center">
          // Nothing to preview — start writing on the left.
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col" data-testid="text-block-editor">
      {/* Tab strip */}
      <div className="flex items-center border border-slate-200 border-b-0 bg-white px-1 pt-1" role="tablist">
        <button
          type="button" role="tab" aria-selected={mode === "edit"}
          onClick={() => setMode("edit")}
          className={tabCls(mode === "edit")}
          data-testid="text-block-tab-edit"
        >
          <Pencil className="w-3 h-3" /> Edit
        </button>
        <button
          type="button" role="tab" aria-selected={mode === "preview"}
          onClick={() => setMode("preview")}
          className={tabCls(mode === "preview")}
          data-testid="text-block-tab-preview"
        >
          <Eye className="w-3 h-3" /> Preview
        </button>
        {/* Split tab is desktop-only; on narrow screens users can still toggle Edit/Preview. */}
        <button
          type="button" role="tab" aria-selected={mode === "split"}
          onClick={() => setMode("split")}
          className={`${tabCls(mode === "split")} hidden lg:inline-flex`}
          data-testid="text-block-tab-split"
          title="Side-by-side edit & preview"
        >
          <Columns className="w-3 h-3" /> Split
        </button>
        <div className="ml-auto pr-2 text-[9px] font-mono-num uppercase tracking-widest text-slate-400 tabular-nums self-center pb-1" data-testid="text-block-char-count">
          {value.length.toLocaleString()} chars
        </div>
      </div>

      {mode === "preview" && previewPane}

      {mode === "edit" && <div className="flex flex-col">{authoring}</div>}

      {mode === "split" && (
        <div className="grid grid-cols-2 gap-3" data-testid="text-block-split">
          <div className="flex flex-col min-w-0">{authoring}</div>
          <div className="min-w-0">
            <div className="border border-slate-200 border-b-0 bg-slate-50 px-2 h-8 flex items-center text-[10px] font-mono-num uppercase tracking-widest text-slate-500">
              // Live preview
            </div>
            <div
              className="bg-white border border-slate-200 rounded-none h-[calc(100%-2rem)] min-h-[16rem] max-h-[36rem] overflow-y-auto p-4"
              data-testid="text-block-split-preview"
            >
              {value.trim() ? (
                renderMiniMarkdown(value)
              ) : (
                <div className="text-[11px] font-mono-num uppercase tracking-widest text-slate-400 py-6 text-center">
                  // Live preview appears here as you type.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
